import { ReactNode } from 'react';
import { styled, Box, Avatar, Typography, Stack } from '@mui/material';

import { MaterialIcon } from '@components';
import PrimarySecondary from '@common/ui/PrimarySecondary';
import { ReportTierOptions } from '@StandingAccess/types';

import { AssessmentTierWidgetLabelHeader } from './AssessmentTierWidget';

const TierWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.border.default}`,
}));

const StepWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const StepAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: 'transparent',
  border: `1px solid ${theme.palette.primary.main}`,

  '&.finished': {
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    color: theme.palette.primary.contrastText,
  },
}));

interface AssessmentRemediationWidgetStepProps {
  text: string;
}

interface AssessmentRemediationWidgetProps {
  type: ReportTierOptions;
  description: string;
  step1: ReactNode;
  step2: ReactNode;
  hideSteps?: boolean;
}

export default function AssessmentRemediationWidget({
  type,
  description,
  step1,
  step2,
  hideSteps = false,
}: AssessmentRemediationWidgetProps) {
  return (
    <TierWrapper>
      <AssessmentTierWidgetLabelHeader type={type} description={description} />
      {!hideSteps && (
        <Stack direction="row" alignItems="center" spacing={2} mt={2}>
          {step1}
          <MaterialIcon symbol="east" color="success" />
          {step2}
        </Stack>
      )}
    </TierWrapper>
  );
}

interface AssessmentRemediationWidgetStepProps {
  step: number;
  text: string;
  action: ReactNode;
  finished?: boolean;
  loading?: boolean;
}

export function AssessmentRemediationWidgetStep({
  step,
  text,
  action,
  finished = false,
}: AssessmentRemediationWidgetStepProps) {
  const stepAvatar = finished ? (
    <StepAvatar className="size-24 finished">
      <MaterialIcon symbol="check" />
    </StepAvatar>
  ) : (
    <StepAvatar className="size-24">
      <Typography variant="helperText" color="primary.main">
        {step}
      </Typography>
    </StepAvatar>
  );

  return (
    <StepWrapper>
      <Stack direction="column" justifyContent="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {stepAvatar}
          <PrimarySecondary primary={text} secondary={`Step ${step}`} inverse />
        </Stack>
        {action}
      </Stack>
    </StepWrapper>
  );
}
